import React, { useContext, useMemo, useState } from "react";
import DateArea from "./DateArea";
import { Box, Modal } from "@mui/material";
import { useTheme } from "@mui/styles";
import DataBlock from "./DataBlock";
import CommunityAnalysisContext from "features/metadesk/CommunityAnalysis/contexts/CommunityAnalysis.context";
import Grid from "@metacrm/metacrm-material-ui/dist/Grid";
import ViewTargetBlock from "./ViewTargetBlock";
import { StyledWhiteContainer } from "features/metadesk/CommunityAnalysis/CommunityAnalysis.styles";
import ChartContainer from "./chart/ChartContainer";
import { useIntl } from "react-intl";
import { get, isEmpty } from "lodash-es";
import {
  AutoCompleteDropdown,
  SIZE,
} from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import useCommunity from "../hooks/useCommunityState.hooks";
import { useDebounceEffect } from "components/ConnectWallet/ConnectWallet";
import { serviceColors } from "features/configure/chartColors";
import { TopMemberAnalytics } from "./TopMemberContent";
import LoadingFilter from "components/LoadingComponent/LoadingFilter";
import BasicDialog from "@metacrm/metacrm-material-ui/dist/Dialog";
import MentionItem from "./chart/MentionItem";
import Button, {
  COLOR,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import { find } from "lodash";
import KeywordTrackingSettingModal from "./KeywordTrackingSettingModal";
import DualYAxisChartContainer from "components/DualYAxisChart/DualYAxisChartContainer";

const PlatformAnalysisContent = ({ type, isConnected }) => {
  const theme = useTheme();
  const { dateRange, timeFrame, setTimeFrame } = useContext(
    CommunityAnalysisContext
  );
  const confirm = useConfirm();
  const colors = serviceColors.small;
  const [isOpenKeywordTrackingSetting, setIsOpenKeywordTrackingSetting] =
    useState(false);
  const [discordColor, telegramColor] = colors;
  const color = type === "discord" ? discordColor : telegramColor;
  const [isError, setIsError] = useState(false);

  const {
    fetchBasicData,
    fetchParticipantsData,
    fetchMessageData,
    fetchReactionData,
    fetchCohortData,
    fetchKeywordData,
    fetchMentionsDetail,
    fetchExcludedUsers,
    manageKeywordFilter,
    manageUserFilter,
    basicData,
    basicDataLoading,
    participantsData,
    participantsDataLoading,
    cohortData,
    cohortDataLoading,
    messageData,
    messagesDataLoading,
    reactionData,
    reactionsDataLoading,
    keywordData,
    keywordDataLoading,
    highLevelDataArray,
    targetMentionsData,
    handleTargetMentionClose,
    initialExcludeWords,
    excludedWords,
    setExcludedWords,
    excludedUsers,
  } = useCommunity();

  const hasAnyIsLoading =
    basicDataLoading ||
    participantsDataLoading ||
    cohortDataLoading ||
    messagesDataLoading ||
    reactionsDataLoading ||
    keywordDataLoading;

  const { formatMessage } = useIntl();
  const timeFrameOptions = [
    {
      label: formatMessage({ id: "dropdown.timeInterval.daily" }),
      value: "days",
    },
    {
      label: formatMessage({ id: "dropdown.timeInterval.weekly" }),
      value: "weeks",
    },
    {
      label: formatMessage({ id: "dropdown.timeInterval.monthly" }),
      value: "months",
    },
  ];

  const handleChangeTimeFrame = (event, value) => {
    setTimeFrame(value.value);
  };

  // 根據 id 決定 tooltip 內容

  const participantsYAxisInfos = [
    {
      label: formatMessage({
        id: "communityAnalysis.label.participantsNumber",
      }),
      key: "participants",
      name: "Participants",
    },
    {
      label: formatMessage({ id: "communityAnalysis.label.newParticipants" }),
      key: "newParticipants",
      name: "New Participants",
    },
  ];
  const participantsChartInfos = [
    {
      title: formatMessage({
        id: "communityAnalysis.chart.participantGrowthTrend",
      }),
      showYAxisDropdown: false,
      type: "line",
      hasData: participantsData.length > 0,
    },
  ];
  const participantsDataForChart = useMemo(() => {
    return {
      xAxisData: participantsData.map((item) => item.date),
      yAxisData: { lineChartData: participantsData },
    };
  }, [participantsData]);

  const messageSeriesData = useMemo(() => {
    return isEmpty(messageData)
      ? []
      : [
          {
            name: formatMessage({
              id: "communityAnalysis.chart.message&voice.legend.messages",
            }), // 為 series 添加名稱，供 legend 顯示
            data: messageData.map((item) => item.messages),
            type: "bar",
            large: true, // 启用大数据量渲染模式
            largeThreshold: 2000, // 超过2000个数据点切换到性能模式
            itemStyle: {
              color: colors[0], // 設定柱狀圖的顏色
            },
          },
        ];
  }, [messageData]);
  const messagesDataForChart = useMemo(() => {
    return {
      showDate: messageData.map((item) => item.date),
      seriesData: messageSeriesData,
    };
  }, [messageData, messageSeriesData]);

  const reactionSeriesData = useMemo(() => {
    return isEmpty(reactionData)
      ? []
      : [
          {
            name: formatMessage({
              id: "communityAnalysis.chart.message&voice.legend.reactions",
            }), // 為 series 添加名稱，供 legend 顯示
            data: reactionData?.map((item) => item.count),
            type: "bar",
            large: true, // 启用大数据量渲染模式
            largeThreshold: 2000, // 超过2000个数据点切换到性能模式
            itemStyle: {
              color: color, // 設定柱狀圖的顏色
            },
          },
        ];
  }, [reactionData]);

  const reactionsDataForChart = useMemo(() => {
    return {
      showDate: reactionData?.map((item) => item.date),
      seriesData: reactionSeriesData,
    };
  }, [reactionData, reactionSeriesData]);

  useDebounceEffect(
    () => {
      if (isConnected) {
        const startDate = dateRange[0];
        const endDate = dateRange[1];
        fetchBasicData({ type, from: startDate, to: endDate });
        fetchParticipantsData({ type, from: startDate, to: endDate });
        fetchReactionData({ type, from: startDate, to: endDate });
        fetchCohortData({ type, from: startDate, to: endDate });
        fetchMessageData({ type, from: startDate, to: endDate });
        fetchKeywordData({ type, from: startDate, to: endDate });
        fetchExcludedUsers({ platform: type });
      }
    },
    50,
    [type, dateRange, isConnected]
  );

  const handleExclude = ({ excludeType, platformType, identifier }) => {
    confirm({
      title: formatMessage({
        id: "communityAnalysis.popup.confirmExclude.title",
      }),
      content: formatMessage({
        id: "communityAnalysis.popup.confirmExclude.description",
      }),
      confirmText: formatMessage({ id: "global.btn.yes" }),
      cancelText: formatMessage({ id: "global.btn.cancel" }),
    })
      .then(async () => {
        switch (excludeType) {
          case "user": {
            handleTargetMentionClose();
            await manageUserFilter({
              platform: platformType,
              userHandle: identifier,
              option: "exclude",
              startDate: dateRange[0],
              endDate: dateRange[1],
            });
            break;
          }
          case "keyword": {
            const isPresent = find(excludedWords, {
              label: identifier.keyword,
            });
            if (!isPresent) {
              setExcludedWords([
                ...excludedWords,
                { label: identifier.keyword, value: identifier._id },
              ]);
            }
            setIsOpenKeywordTrackingSetting(true);
            break;
          }
        }
      })
      .catch((err) => {
        console.log("cancel", err);
      });
  };

  const handleCreateNewWordFilter = async (value) => {
    if (!value) return;

    if (value?.length <= 2 || value?.length >= 30) {
      setIsError(true);
      return;
    }

    setIsError(false);
    const isPresent = find(excludedWords, {
      label: value,
    });
    if (!isPresent) {
      setExcludedWords([...excludedWords, { label: value, value: value }]);
    }
  };

  return (
    <Box sx={{ position: "relative" }} className="fade-up-more">
      {hasAnyIsLoading && <LoadingFilter />}
      <DateArea
        sx={{ marginBottom: "8px" }}
        context={CommunityAnalysisContext}
      />
      <Box component={Grid} container spacing={1} mb={1}>
        <Grid item lg={3} xs={6}>
          <ViewTargetBlock
            targetName={basicData?.serverName || basicData?.groupName}
            isLoading={basicDataLoading}
          ></ViewTargetBlock>
        </Grid>
        {highLevelDataArray.length > 0
          ? highLevelDataArray.map((highLevelData, index) => (
              <Grid item lg={3} xs={6} key={index}>
                <DataBlock
                  data={highLevelData}
                  isLoading={basicDataLoading}
                ></DataBlock>
              </Grid>
            ))
          : Array.from({ length: 3 }).map((index) => (
              <Grid item lg={3} xs={6} key={index}>
                <DataBlock
                  data={{ isEmpty: true }}
                  isLoading={basicDataLoading}
                ></DataBlock>
              </Grid>
            ))}
      </Box>

      <Box component={Grid} container spacing={1}>
        <Grid item xs={12} lg={6}>
          <StyledWhiteContainer>
            <DualYAxisChartContainer
              data={participantsDataForChart}
              yAxisInfos={participantsYAxisInfos}
              selectedYAxis={["participants", "newParticipants"]}
              colors={colors}
              chartInfos={participantsChartInfos}
              isLoading={participantsDataLoading}
              customStyle={{ containerStyle: { height: "330px" } }}
              otherCustomSetting={{
                formatXAxisLabel: { open: true, format: "DD MMM" },
              }}
            />
          </StyledWhiteContainer>
        </Grid>

        <Grid item xs={12} lg={6}>
          <StyledWhiteContainer>
            <ChartContainer
              chartStyle="heatmap"
              tooltipText={formatMessage({
                id: "communityAnalysis.chart.retentionCohort.tooltip",
              })}
              title={formatMessage({
                id: "communityAnalysis.chart.retentionCohort",
              })}
              chartData={cohortData}
              isLoading={cohortDataLoading}
              heatColor={color}
              context={CommunityAnalysisContext}
            >
              <Box width={"160px"}>
                <AutoCompleteDropdown
                  value={timeFrameOptions?.find(
                    (option) => option.value === timeFrame
                  )}
                  placeholder={formatMessage({ id: "menu.placeholder.select" })}
                  options={timeFrameOptions}
                  onChange={handleChangeTimeFrame}
                  name="utmId"
                  size={SIZE.MEDIUM}
                />
              </Box>
            </ChartContainer>
          </StyledWhiteContainer>
        </Grid>

        <Grid item xs={12} lg={6}>
          <StyledWhiteContainer>
            <ChartContainer
              chartData={messagesDataForChart}
              tooltipText={formatMessage({
                id: "communityAnalysis.chart.message&voice.tooltip",
              })}
              chartStyle="bar"
              title={formatMessage({
                id: "communityAnalysis.chart.message&voice",
              })}
              isLoading={messagesDataLoading}
            ></ChartContainer>
          </StyledWhiteContainer>
        </Grid>

        <Grid item xs={12} lg={6}>
          <StyledWhiteContainer>
            <ChartContainer
              chartStyle="bar"
              tooltipText={formatMessage({
                id: "communityAnalysis.chart.reaction.tooltip",
              })}
              chartData={reactionsDataForChart}
              title={formatMessage({
                id: "communityAnalysis.chart.reactionGrowth",
              })}
              isLoading={reactionsDataLoading}
            ></ChartContainer>
          </StyledWhiteContainer>
        </Grid>

        <Box component={Grid} item xs={12} mt={3} mb="12px">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ fontWeight: 600, fontSize: "20px" }}>
              {formatMessage({
                id: "communityAnalysis.keywordTracking.title",
              })}
            </Box>
            <Button
              color={COLOR.SECONDARY}
              onClick={() => {
                setIsOpenKeywordTrackingSetting(true);
              }}
              style={{ minWidth: 36, height: 36, width: 36, padding: 0 }}
            >
              <i className="meta-crm-icon-ic_settingLine font-size-18" />
            </Button>
          </Box>
        </Box>

        <Box
          component={Grid}
          sx={{
            marginBottom: { xs: "12px", lg: "0px" },
          }}
          item
          xs={12}
          lg={5}
        >
          <StyledWhiteContainer style={{ padding: 0 }}>
            <ChartContainer
              chartStyle="table"
              chartData={keywordData.rank}
              isLoading={keywordDataLoading}
              type={type}
              height="386px"
              actions={{ fetchMentionsDetail, handleExclude }}
            />
          </StyledWhiteContainer>
        </Box>

        <Grid item xs={12} lg={7}>
          <StyledWhiteContainer>
            <ChartContainer
              chartStyle="tagCloud"
              title={formatMessage({
                id: "communityAnalysis.chart.keywordCloud",
              })}
              type={type}
              chartData={keywordData.cloud}
              isLoading={keywordDataLoading}
              actions={{ fetchMentionsDetail, handleExclude }}
              height="350px"
            />
          </StyledWhiteContainer>
        </Grid>

        <Box component={Grid} item xs={12} mt={3} mb="0px">
          <Box sx={{ fontWeight: 600, fontSize: "20px" }}>
            {formatMessage({ id: "cummunityAnalysis.topMembers.title" })}
          </Box>
        </Box>

        <Grid item xs={12}>
          <TopMemberAnalytics
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            platform={type}
            excludedUsers={excludedUsers}
          />
        </Grid>
      </Box>

      {targetMentionsData && (
        <BasicDialog
          open={Boolean(targetMentionsData)}
          show={Boolean(targetMentionsData)}
          onClose={handleTargetMentionClose}
          dialogRootStyle={{ width: "950px", maxWidth: "950px" }}
        >
          <Box
            padding="48px 40px 50px"
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "18px",
                marginBottom: "16px",
              }}
            >
              <Box
                sx={{ fontSize: "18px", fontWeight: theme.fontWeight.BOLD }}
                mr={"8px"}
              >
                {targetMentionsData?.name}
                {":"}
              </Box>
              <Box
                sx={{
                  fontSize: "18px",
                  fontWeight: theme.fontWeight.BOLD,
                  color: theme.customColors.purple[500],
                }}
              >
                {`${get(targetMentionsData, "times", 0)} Mentions`}
              </Box>
            </Box>
            <Box
              sx={{
                borderRadius: "8px",
                border: `1px solid ${theme.customColors.grey[300]}`,
                padding: "16px",
                overflow: "auto",
              }}
            >
              {targetMentionsData &&
                targetMentionsData?.mentions?.map((item, i) => (
                  <MentionItem
                    key={i}
                    senderHandle={item.senderHandle}
                    content={item.content}
                    date={item.date}
                    keyword={targetMentionsData?.name}
                    handleExcludeUser={handleExclude}
                    platform={type}
                  ></MentionItem>
                ))}
            </Box>
          </Box>
        </BasicDialog>
      )}
      <KeywordTrackingSettingModal
        isOpen={isOpenKeywordTrackingSetting}
        setIsOpen={setIsOpenKeywordTrackingSetting}
        theme={theme}
        excludedWords={excludedWords}
        excludedUsers={excludedUsers}
        handleChangeWordFilter={(event, value) => {
          setExcludedWords([...value]);
        }}
        handleCreateNewWordFilter={handleCreateNewWordFilter}
        handleCancel={() => {
          setExcludedWords([...initialExcludeWords]);
        }}
        manageKeywordFilter={manageKeywordFilter}
        manageUserFilter={manageUserFilter}
        dateRange={dateRange}
        isError={isError}
        type={type}
      />
    </Box>
  );
};

export default PlatformAnalysisContent;
